import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, Users, Calendar, CreditCard, Bell, LogOut, 
  Menu, X, Check, Percent, Edit3, Save, FileText, Edit2, Settings, ToggleLeft, ToggleRight,
  MessageCircle, Send, Eye, Trash2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Wrench, Tag, MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";
import { useAppSettings } from "@/context/SettingsContext";

const API = "/api";

const Subscriptions = () => {
  const { updateSettings: updateAppSettings } = useAppSettings();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const [customers, setCustomers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedFrequency, setSelectedFrequency] = useState("1");
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", price: "", discount_percent: "" });
  const [saving, setSaving] = useState(false);
  const [pendingQuotes, setPendingQuotes] = useState(0);
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(true);
  const [togglingSubscriptions, setTogglingSubscriptions] = useState(false);
  const [stripeCheckoutEnabled, setStripeCheckoutEnabled] = useState(true);
  const [togglingStripe, setTogglingStripe] = useState(false);
  const [additionalServicesEnabled, setAdditionalServicesEnabled] = useState(true);
  const [togglingAdditionalServices, setTogglingAdditionalServices] = useState(false);
  const [neighborhoodDiscountEnabled, setNeighborhoodDiscountEnabled] = useState(true);
  const [togglingNeighborhoodDiscount, setTogglingNeighborhoodDiscount] = useState(false);

  // Vonage SMS config
  const [vonageConfig, setVonageConfig] = useState({ api_key: "", api_secret: "", from_number: "", enabled: false });
  const [savingVonage, setSavingVonage] = useState(false);
  const [vonageExpanded, setVonageExpanded] = useState(new URLSearchParams(window.location.search).get("tab") === "settings");
  const [alertNumbers, setAlertNumbers] = useState("");
  const [testingText, setTestingText] = useState(false);

  // Gmail SMTP config
  const [gmailConfig, setGmailConfig] = useState({ email: "zigzaglawn@gmail.com", password: "", enabled: false });
  const [savingGmail, setSavingGmail] = useState(false);
  const [gmailExpanded, setGmailExpanded] = useState(false);
  
  // Coupon editor state
  const [couponEditing, setCouponEditing] = useState(false);
  const [couponSaving, setCouponSaving] = useState(false);
  const [couponFields, setCouponFields] = useState({
    badge: "SPECIAL OFFER",
    title: "Neighborhood Discount",
    subtitle: "Save when your neighbors book too!",
    discount_text: "Up to 15% OFF",
    description: "If we're already in your neighborhood, you get a discount — we only unload the mower once!",
    cta_button: "Claim Discount",
    fine_print: "Applies when 2+ homes in the same area book together",
  });
  
  // Edit subscription state
  const [editSubDialogOpen, setEditSubDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [newPlanForEdit, setNewPlanForEdit] = useState("");
  const [newFrequencyForEdit, setNewFrequencyForEdit] = useState("1");
  const [savingSubEdit, setSavingSubEdit] = useState(false);
  
  // Chat state
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [chatCustomer, setChatCustomer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState({});
  const chatEndRef = useRef(null);

  // Payment calendar state
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [paymentData, setPaymentData] = useState({});
  const [paymentYear, setPaymentYear] = useState(new Date().getFullYear());
  const [togglingPayment, setTogglingPayment] = useState(null);

  const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];


  const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, subsRes, plansRes, quotesRes, settingsRes, unreadRes, cmsRes] = await Promise.all([
        axios.get(`${API}/customers/with-quotes`, { withCredentials: true }),
        axios.get(`${API}/subscriptions`, { withCredentials: true }),
        axios.get(`${API}/subscription/plans/all`, { withCredentials: true }),
        axios.get(`${API}/quotes`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/settings`, { withCredentials: true }).catch(() => ({ data: { subscriptions_enabled: true } })),
        axios.get(`${API}/customers/with-unread-messages`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/cms/content`, { withCredentials: true }).catch(() => ({ data: { content: {}, defaults: {} } })),
      ]);
      setCustomers(customersRes.data);
      setSubscriptions(subsRes.data);
      setPlans(plansRes.data);
      setPendingQuotes(quotesRes.data.filter(q => q.status === "pending").length);
      setSubscriptionsEnabled(settingsRes.data.subscriptions_enabled !== false);
      setStripeCheckoutEnabled(settingsRes.data.stripe_checkout_enabled !== false);
      setAdditionalServicesEnabled(settingsRes.data.additional_services_enabled !== false);
      setNeighborhoodDiscountEnabled(settingsRes.data.neighborhood_discount_enabled !== false);
      setVonageConfig({
        api_key: settingsRes.data.vonage_api_key || "",
        api_secret: settingsRes.data.vonage_api_secret || "",
        from_number: settingsRes.data.vonage_from_number || "",
        enabled: settingsRes.data.vonage_enabled || false,
      });
      setAlertNumbers((settingsRes.data.alert_phone_numbers || []).join(", "));
      setGmailConfig({
        email: settingsRes.data.gmail_email || "zigzaglawn@gmail.com",
        password: settingsRes.data.gmail_password || "",
        enabled: settingsRes.data.gmail_enabled || false,
      });
      setUnreadMessages(unreadRes.data);
      // Load coupon CMS fields
      const cmsData = cmsRes.data;
      const defaults = cmsData.defaults?.landing?.neighborhood_discount || {};
      const saved = cmsData.content?.landing?.neighborhood_discount || {};
      setCouponFields(prev => ({
        badge: saved.badge || defaults.badge || prev.badge,
        title: saved.title || defaults.title || prev.title,
        subtitle: saved.subtitle || defaults.subtitle || prev.subtitle,
        discount_text: saved.discount_text || defaults.discount_text || prev.discount_text,
        description: saved.description || defaults.description || prev.description,
        cta_button: saved.cta_button || defaults.cta_button || prev.cta_button,
        fine_print: saved.fine_print || defaults.fine_print || prev.fine_print,
      }));
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!selectedCustomer || !selectedPlan) {
      toast.error("Please select customer and plan");
      return;
    }
    try {
      await axios.post(`${API}/subscriptions`, {
        customer_id: selectedCustomer,
        plan: selectedPlan,
        frequency: parseInt(selectedFrequency),
      }, { withCredentials: true });
      toast.success("Subscription created successfully");
      setDialogOpen(false);
      setSelectedCustomer("");
      setSelectedPlan("");
      setSelectedFrequency("1");
      fetchData();
    } catch (error) {
      console.error("Failed to create subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to create subscription");
    }
  };

  const handleCancelSubscription = async (customerId) => {
    if (!window.confirm("Are you sure you want to cancel this subscription?")) return;
    try {
      await axios.delete(`${API}/subscriptions/${customerId}`, { withCredentials: true });
      toast.success("Subscription cancelled");
      fetchData();
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      toast.error("Failed to cancel subscription");
    }
  };

  const openEditDialog = (planId) => {
    const plan = plans[planId];
    setEditingPlan(planId);
    setEditForm({
      name: plan.name,
      price: plan.price.toString(),
      discount_percent: plan.discount_percent.toString(),
    });
    setEditDialogOpen(true);
  };

  const handleSavePlan = async () => {
    if (!editingPlan) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/subscription/plans/${editingPlan}`, {
        name: editForm.name,
        price: parseFloat(editForm.price),
        discount_percent: parseInt(editForm.discount_percent),
      }, { withCredentials: true });
      
      toast.success("Plan updated successfully");
      setEditDialogOpen(false);
      setEditingPlan(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update plan:", error);
      toast.error(error.response?.data?.detail || "Failed to update plan");
    } finally {
      setSaving(false);
    }
  };

  const customersWithSubscription = customers.filter(c => c.subscription_plan !== "none");
  const customersWithoutSubscription = customers.filter(c => c.subscription_plan === "none");

  // Calculate total monthly revenue based on customer's actual subscription frequency
  const calculateMonthlyRevenue = () => {
    let total = 0;
    customersWithSubscription.forEach(customer => {
      const planId = customer.subscription_plan;
      const quote = customer.latest_quote;
      const freq = customer.subscription_frequency || 1;
      if (planId === "monthly" && quote) {
        const price = freq === 2 ? (quote.monthly_2x_price || quote.monthly_1x_price || quote.quoted_price || 0) : (quote.monthly_1x_price || quote.quoted_price || 0);
        total += price;
      } else if (planId === "yearly" && quote) {
        const yearlyPrice = freq === 2 ? (quote.yearly_2x_price || quote.yearly_1x_price || 0) : (quote.yearly_1x_price || 0);
        total += yearlyPrice > 0 ? yearlyPrice / 12 : (quote.quoted_price || 0);
      }
    });
    return total;
  };

  const monthlyRevenue = calculateMonthlyRevenue();

  const handleToggleSubscriptions = async (enabled) => {
    setTogglingSubscriptions(true);
    try {
      await updateAppSettings({ subscriptions_enabled: enabled });
      setSubscriptionsEnabled(enabled);
      toast.success(enabled ? "Subscriptions are now visible to customers" : "Subscriptions are now hidden from customers");
    } catch (error) {
      console.error("Failed to update settings:", error);
      toast.error("Failed to update settings");
    } finally {
      setTogglingSubscriptions(false);
    }
  };

  const handleToggleStripeCheckout = async (enabled) => {
    setTogglingStripe(true);
    try {
      await axios.put(`${API}/settings`, { stripe_checkout_enabled: enabled }, { withCredentials: true });
      setStripeCheckoutEnabled(enabled);
      toast.success(enabled 
        ? "Stripe checkout enabled - customers will pay directly" 
        : "Request mode enabled - customers will send you a message instead"
      );
    } catch (error) {
      console.error("Failed to update settings:", error);
      toast.error("Failed to update settings");
    } finally {
      setTogglingStripe(false);
    }
  };

  const handleToggleAdditionalServices = async (enabled) => {
    setTogglingAdditionalServices(true);
    try {
      await axios.put(`${API}/settings`, { additional_services_enabled: enabled }, { withCredentials: true });
      setAdditionalServicesEnabled(enabled);
      toast.success(enabled ? "Additional Services section is now visible" : "Additional Services section is now hidden");
    } catch {
      toast.error("Failed to update settings");
    } finally {
      setTogglingAdditionalServices(false);
    }
  };

  const handleToggleNeighborhoodDiscount = async (enabled) => {
    setTogglingNeighborhoodDiscount(true);
    try {
      await axios.put(`${API}/settings`, { neighborhood_discount_enabled: enabled }, { withCredentials: true });
      setNeighborhoodDiscountEnabled(enabled);
      toast.success(enabled ? "Neighborhood Discount coupon is now visible" : "Neighborhood Discount coupon is now hidden");
    } catch {
      toast.error("Failed to update settings");
    } finally {
      setTogglingNeighborhoodDiscount(false);
    }
  };

  const handleSaveCoupon = async () => {
    setCouponSaving(true);
    try {
      const updates = Object.entries(couponFields).map(([key, value]) => ({
        page: "landing",
        section: "neighborhood_discount",
        content_key: key,
        value,
      }));
      await axios.put(`${API}/cms/content/bulk`, { updates }, { withCredentials: true });
      setCouponEditing(false);
      toast.success("Coupon content updated!");
    } catch {
      toast.error("Failed to save coupon content");
    } finally {
      setCouponSaving(false);
    }
  };

  const handleSaveVonage = async () => {
    setSavingVonage(true);
    const complete = vonageConfig.api_key.trim() && vonageConfig.api_secret.trim() && vonageConfig.from_number.trim();
    const numbers = alertNumbers.split(/[,\n]/).map((n) => n.trim()).filter(Boolean);
    try {
      await axios.put(`${API}/settings`, {
        vonage_api_key: vonageConfig.api_key.trim(),
        vonage_api_secret: vonageConfig.api_secret.trim(),
        vonage_from_number: vonageConfig.from_number.trim(),
        vonage_enabled: complete ? true : vonageConfig.enabled,
        ...(numbers.length ? { alert_phone_numbers: numbers } : {}),
      }, { withCredentials: true });
      if (complete) setVonageConfig((p) => ({ ...p, enabled: true }));
      toast.success(complete ? "Vonage saved and switched on. Tap Send Test Text to check it." : "Vonage settings saved");
    } catch {
      toast.error("Failed to save Vonage settings");
    } finally {
      setSavingVonage(false);
    }
  };

  const handleTestText = async () => {
    setTestingText(true);
    try {
      const res = await axios.post(`${API}/admin/test-sms-alert`, {}, { withCredentials: true });
      const results = res.data.results || [];
      const failed = results.filter((r) => r.error);
      if (failed.length === 0 && results.length) toast.success(`Test text sent to ${results.length} number(s)`);
      else toast.error(`Test text failed: ${failed[0]?.error || "unknown error"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test text failed");
    } finally {
      setTestingText(false);
    }
  };

  const handleSaveGmail = async () => {
    setSavingGmail(true);
    try {
      await axios.put(`${API}/settings`, {
        gmail_email: gmailConfig.email,
        gmail_password: gmailConfig.password,
        gmail_enabled: gmailConfig.enabled,
      }, { withCredentials: true });
      toast.success("Gmail settings saved!");
      setGmailExpanded(false);
    } catch {
      toast.error("Failed to save Gmail settings");
    } finally {
      setSavingGmail(false);
    }
  };

  // Edit subscription handlers
  const openEditSubscription = (customer) => {
    setEditingSubscription(customer);
    setNewPlanForEdit(customer.subscription_plan);
    setNewFrequencyForEdit(String(customer.subscription_frequency || 1));
    setEditSubDialogOpen(true);
  };

  const handleSaveSubscriptionEdit = async () => {
    if (!editingSubscription || !newPlanForEdit) return;
    setSavingSubEdit(true);
    try {
      await axios.put(`${API}/subscriptions/${editingSubscription.customer_id}`, {
        plan: newPlanForEdit,
        frequency: parseInt(newFrequencyForEdit),
      }, { withCredentials: true });
      toast.success("Subscription updated successfully");
      setEditSubDialogOpen(false);
      setEditingSubscription(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to update subscription");
    } finally {
      setSavingSubEdit(false);
    }
  };

  // Chat handlers
  const openChat = async (customer) => {
    setChatCustomer(customer);
    setChatDialogOpen(true);
    try {
      const response = await axios.get(`${API}/customers/${customer.customer_id}/messages`, { withCredentials: true });
      setMessages(response.data);
      // Mark messages as read
      await axios.put(`${API}/customers/${customer.customer_id}/messages/mark-read`, {}, { withCredentials: true });
      // Update unread count
      setUnreadMessages(prev => ({ ...prev, [customer.customer_id]: 0 }));
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !chatCustomer) return;
    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/customers/${chatCustomer.customer_id}/messages`, {
        customer_id: chatCustomer.customer_id,
        message: newMessage,
        message_type: "chat"
      }, { withCredentials: true });
      setMessages(prev => [...prev, response.data]);
      setNewMessage("");
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!chatCustomer || !window.confirm("Delete this message?")) return;
    
    try {
      await axios.delete(
        `${API}/customers/${chatCustomer.customer_id}/messages/${messageId}`,
        { withCredentials: true }
      );
      setMessages(messages.filter(m => m.message_id !== messageId));
      toast.success("Message deleted");
    } catch (error) {
      console.error("Failed to delete message:", error);
      toast.error("Failed to delete message");
    }
  };

  // Payment calendar handlers
  const togglePaymentCalendar = async (customerId) => {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
      return;
    }
    setExpandedCustomer(customerId);
    await fetchPayments(customerId, paymentYear);
  };

  const fetchPayments = async (customerId, year) => {
    try {
      const res = await axios.get(`${API}/subscription-payments/${customerId}?year=${year}`, { withCredentials: true });
      setPaymentData(prev => ({ ...prev, [`${customerId}_${year}`]: res.data.payments }));
    } catch (error) {
      console.error("Failed to fetch payments:", error);
    }
  };

  const toggleMonthPayment = async (customerId, month, cutNumber = 0) => {
    const key = `${customerId}_${paymentYear}`;
    const currentPayments = paymentData[key] || {};
    const paymentKey = cutNumber ? `${month}_${cutNumber}` : `${month}`;
    const currentlyPaid = currentPayments[paymentKey]?.paid || false;
    const newPaid = !currentlyPaid;
    
    setTogglingPayment(`${customerId}_${month}_${cutNumber}`);
    try {
      await axios.post(`${API}/subscription-payments/${customerId}`, {
        year: paymentYear,
        month: month,
        paid: newPaid,
        cut_number: cutNumber,
      }, { withCredentials: true });
      
      setPaymentData(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          [paymentKey]: { paid: newPaid, paid_at: newPaid ? new Date().toISOString() : null, cut_number: cutNumber }
        }
      }));
      const cutLabel = cutNumber ? ` (Cut ${cutNumber})` : "";
      toast.success(`${MONTH_NAMES[month - 1]}${cutLabel} ${paymentYear} marked as ${newPaid ? "paid" : "unpaid"}`);
    } catch (error) {
      console.error("Failed to toggle payment:", error);
      toast.error("Failed to update payment status");
    } finally {
      setTogglingPayment(null);
    }
  };

  const changePaymentYear = async (direction) => {
    const newYear = paymentYear + direction;
    setPaymentYear(newYear);
    if (expandedCustomer) {
      await fetchPayments(expandedCustomer, newYear);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-[#27272A] p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden text-[#A1A1AA] hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase">{subscriptionsEnabled ? "Subscriptions" : "Settings"}</h1>
                {subscriptionsEnabled && <p className="text-[#A1A1AA] text-sm">{customersWithSubscription.length} active subscriptions</p>}
              </div>
            </div>
            <div className={`items-center gap-4 ${subscriptionsEnabled ? "flex" : "hidden"}`}>
              {/* Monthly Revenue Card */}
              <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-[#064E3B] border border-[#39FF14]/30 rounded-xl">
                <div className="w-10 h-10 bg-[#39FF14]/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-[#39FF14]" />
                </div>
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase">Monthly Revenue</p>
                  <p className="text-[#39FF14] font-bold text-xl" data-testid="monthly-revenue">${monthlyRevenue.toFixed(2)}</p>
                </div>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="add-subscription-btn"
                  className="bg-[#39FF14] text-black hover:bg-[#32D711] hover:shadow-[0_0_15px_rgba(57,255,20,0.6)] transition-all font-bold rounded-full"
                  disabled={customersWithoutSubscription.length === 0}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Add Subscription
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl uppercase">Add Subscription</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#A1A1AA]">Customer</Label>
                    <Select value={selectedCustomer} onValueChange={v => { setSelectedCustomer(v); setSelectedPlan(""); setSelectedFrequency("1"); }}>
                      <SelectTrigger data-testid="subscription-customer-select" className="bg-[#0A0A0A] border-[#27272A] text-white">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {customersWithoutSubscription.map((customer) => (
                          <SelectItem 
                            key={customer.customer_id} 
                            value={customer.customer_id}
                            className="text-white hover:bg-[#27272A]"
                          >
                            {customer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Plan</Label>
                    <Select value={selectedPlan} onValueChange={v => { setSelectedPlan(v); setSelectedFrequency("1"); }}>
                      <SelectTrigger data-testid="subscription-plan-select" className="bg-[#0A0A0A] border-[#27272A] text-white">
                        <SelectValue placeholder="Select plan" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {Object.entries(plans).map(([planId, plan]) => (
                          <SelectItem 
                            key={planId} 
                            value={planId}
                            className="text-white hover:bg-[#27272A]"
                          >
                            {plan.name} - {plan.discount_percent}% Discount
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedPlan && (
                    <div>
                      <Label className="text-[#A1A1AA]">Frequency</Label>
                      <Select value={selectedFrequency} onValueChange={setSelectedFrequency}>
                        <SelectTrigger data-testid="subscription-frequency-select" className="bg-[#0A0A0A] border-[#27272A] text-white">
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#27272A]">
                          <SelectItem value="1" className="text-white hover:bg-[#27272A]">
                            1x per month
                          </SelectItem>
                          <SelectItem value="2" className="text-white hover:bg-[#27272A]">
                            2x per month
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {/* Price Preview from quote */}
                  {selectedCustomer && selectedPlan && (() => {
                    const cust = customersWithoutSubscription.find(c => c.customer_id === selectedCustomer);
                    const q = cust?.latest_quote;
                    const priceKey = `${selectedPlan}_${selectedFrequency}x_price`;
                    const price = q?.[priceKey];
                    return price ? (
                      <div className="bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-lg p-3 text-center" data-testid="subscription-price-preview">
                        <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">Quoted Price</p>
                        <p className="text-[#39FF14] text-2xl font-black mt-1">
                          ${Number(price).toLocaleString()}
                          <span className="text-sm font-normal text-[#A1A1AA]">/{selectedPlan === "monthly" ? "mo" : "yr"}</span>
                        </p>
                        <p className="text-[#52525B] text-xs mt-1">
                          {selectedPlan === "monthly" ? "Monthly" : "Yearly"} · {selectedFrequency}x/month
                        </p>
                      </div>
                    ) : (
                      <div className="bg-[#27272A]/50 rounded-lg p-3 text-center">
                        <p className="text-[#A1A1AA] text-xs">No quote on file for this combination</p>
                      </div>
                    );
                  })()}
                  <Button 
                    data-testid="confirm-subscription-btn"
                    className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                    onClick={handleCreateSubscription}
                  >
                    Create Subscription
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {/* Mobile Revenue Card */}
          <div className={`sm:hidden mb-4 p-4 bg-[#064E3B] border border-[#39FF14]/30 rounded-xl ${subscriptionsEnabled ? "" : "hidden"}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#39FF14]/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-[#39FF14]" />
                </div>
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase">Monthly Revenue</p>
                  <p className="text-[#39FF14] font-bold text-xl">${monthlyRevenue.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <Tabs key={`${subscriptionsEnabled ? "on" : "off"}${location.search}`} defaultValue={!subscriptionsEnabled || new URLSearchParams(location.search).get("tab") === "settings" ? "settings" : "subscribers"} className="w-full">
              <TabsList className="bg-[#121212] border border-[#27272A] mb-6">
                {subscriptionsEnabled && <TabsTrigger 
                  value="subscribers" 
                  className="data-[state=active]:bg-[#39FF14]/20 data-[state=active]:text-[#39FF14]"
                >
                  Subscribers
                </TabsTrigger>}
                {subscriptionsEnabled && <TabsTrigger 
                  value="plans" 
                  className="data-[state=active]:bg-[#39FF14]/20 data-[state=active]:text-[#39FF14]"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Plans & Pricing
                </TabsTrigger>}
                <TabsTrigger 
                  value="settings" 
                  className="data-[state=active]:bg-[#39FF14]/20 data-[state=active]:text-[#39FF14]"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </TabsTrigger>
              </TabsList>

              {/* Subscribers Tab */}
              <TabsContent value="subscribers">
                {/* Subscription Plans Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {Object.entries(plans).map(([planId, plan]) => (
                    <div 
                      key={planId}
                      className={`bg-[#121212] border rounded-xl p-6 ${
                        planId === "yearly" 
                          ? "border-[#39FF14] shadow-[0_0_15px_rgba(57,255,20,0.1)]" 
                          : "border-[#27272A]"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            planId === "yearly" ? "bg-[#39FF14]" : "bg-[#064E3B]"
                          }`}>
                            <Percent className={`w-6 h-6 ${planId === "yearly" ? "text-black" : "text-[#39FF14]"}`} />
                          </div>
                          <div>
                            <h3 className="font-heading text-xl font-bold text-white uppercase">{plan.name}</h3>
                            <p className="text-[#A1A1AA] text-sm">{plan.discount_percent}% discount applied</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#A1A1AA] hover:text-[#39FF14]"
                          onClick={() => openEditDialog(planId)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 text-[#39FF14]">
                        <span className="text-3xl font-bold">
                          {customersWithSubscription.filter(c => c.subscription_plan === planId).length}
                        </span>
                        <span className="text-[#A1A1AA]">subscribers</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Active Subscriptions */}
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <h2 className="font-heading text-xl font-bold text-white uppercase mb-6">Active Subscriptions</h2>
                  
                  {customersWithSubscription.length === 0 ? (
                    <div className="text-center py-12">
                      <CreditCard className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
                      <p className="text-[#A1A1AA]">No active subscriptions</p>
                      <p className="text-[#A1A1AA] text-sm mt-2">Add a subscription to give customers discounts</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {customersWithSubscription.map((customer) => {
                        const plan = plans[customer.subscription_plan];
                        const hasUnread = unreadMessages[customer.customer_id] > 0;
                        const isExpanded = expandedCustomer === customer.customer_id;
                        const paymentKey = `${customer.customer_id}_${paymentYear}`;
                        const customerPayments = paymentData[paymentKey] || {};
                        const paidCount = Object.values(customerPayments).filter(p => p?.paid).length;
                        const freq = customer.subscription_frequency || 1;
                        const totalSlots = freq === 2 ? 24 : 12;
                        const quote = customer.latest_quote;
                        
                        // Calculate what this customer is paying based on their actual frequency
                        let subscribedPrice = null;
                        let priceLabel = "";
                        let priceMissing = false;
                        if (customer.subscription_plan === "monthly" && quote) {
                          if (freq === 2) {
                            subscribedPrice = quote.monthly_2x_price;
                            if (!subscribedPrice) { subscribedPrice = quote.monthly_1x_price ? quote.monthly_1x_price * 2 : quote.quoted_price; priceMissing = !quote.monthly_2x_price; }
                          } else {
                            subscribedPrice = quote.monthly_1x_price || quote.quoted_price;
                          }
                          priceLabel = freq === 2 ? "2x/mo" : "1x/mo";
                        } else if (customer.subscription_plan === "yearly" && quote) {
                          if (freq === 2) {
                            subscribedPrice = quote.yearly_2x_price;
                            if (!subscribedPrice) { subscribedPrice = quote.yearly_1x_price ? quote.yearly_1x_price * 2 : null; priceMissing = !quote.yearly_2x_price; }
                          } else {
                            subscribedPrice = quote.yearly_1x_price || quote.yearly_2x_price;
                          }
                          priceLabel = freq === 2 ? "2x/mo" : "1x/mo";
                        }
                        
                        return (
                          <div key={customer.customer_id} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg overflow-hidden hover:border-[#39FF14]/30 transition-colors">
                            {/* Subscriber Row */}
                            <div 
                              data-testid={`subscription-${customer.customer_id}`}
                              className="flex items-center justify-between p-4"
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-[#064E3B] rounded-full flex items-center justify-center relative">
                                  <span className="text-[#39FF14] font-bold text-lg">
                                    {customer.name.charAt(0).toUpperCase()}
                                  </span>
                                  {hasUnread && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                      {unreadMessages[customer.customer_id]}
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <h3 className="text-white font-semibold">{customer.name}</h3>
                                  <p className="text-[#A1A1AA] text-sm">{customer.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {/* Subscription Price Display */}
                                {subscribedPrice ? (
                                  <div className="text-right mr-2">
                                    <p className={`font-bold text-lg ${customer.subscription_plan === "yearly" ? "text-[#39FF14]" : "text-blue-400"}`}>
                                      ${customer.subscription_plan === "yearly" ? (subscribedPrice / 12).toFixed(2) : subscribedPrice.toFixed(2)}
                                      <span className="text-xs font-normal text-[#A1A1AA]">/mo</span>
                                    </p>
                                    <p className="text-[#A1A1AA] text-xs">
                                      {customer.subscription_plan === "yearly" 
                                        ? `$${subscribedPrice.toFixed(2)}/yr (${priceLabel})` 
                                        : priceLabel}
                                    </p>
                                    {priceMissing && <p className="text-yellow-400 text-[10px]">est. (update quote)</p>}
                                  </div>
                                ) : (
                                  <div className="text-right mr-2">
                                    <p className="text-[#A1A1AA] text-xs">No quote</p>
                                  </div>
                                )}
                                <div className={`px-4 py-2 rounded-full flex items-center gap-2 ${
                                  customer.subscription_plan === "yearly" 
                                    ? "bg-[#39FF14] text-black" 
                                    : "bg-[#064E3B] text-[#39FF14]"
                                }`}>
                                  <Check className="w-4 h-4" />
                                  <span className="font-bold text-sm uppercase">
                                    {plan?.name || customer.subscription_plan} - {plan?.discount_percent || 10}% off
                                  </span>
                                </div>
                                <Button
                                  data-testid={`payment-calendar-${customer.customer_id}`}
                                  variant="outline"
                                  size="sm"
                                  className={`border-[#39FF14]/30 hover:bg-[#39FF14]/10 ${isExpanded ? 'text-[#39FF14] bg-[#39FF14]/10' : 'text-[#A1A1AA]'}`}
                                  onClick={() => togglePaymentCalendar(customer.customer_id)}
                                >
                                  <Calendar className="w-4 h-4 mr-1" />
                                  <span className="hidden sm:inline text-xs">Payments</span>
                                  {isExpanded ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                                </Button>
                                <Button
                                  data-testid={`chat-${customer.customer_id}`}
                                  variant="outline"
                                  size="sm"
                                  className={`border-[#39FF14]/30 text-[#39FF14] hover:bg-[#39FF14]/10 relative ${hasUnread ? 'animate-pulse' : ''}`}
                                  onClick={() => openChat(customer)}
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  data-testid={`edit-subscription-${customer.customer_id}`}
                                  variant="outline"
                                  size="sm"
                                  className="border-blue-400/30 text-blue-400 hover:bg-blue-400/10"
                                  onClick={() => openEditSubscription(customer)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  data-testid={`cancel-subscription-${customer.customer_id}`}
                                  variant="outline"
                                  size="sm"
                                  className="border-red-400/30 text-red-400 hover:bg-red-400/10"
                                  onClick={() => handleCancelSubscription(customer.customer_id)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                            
                            {/* Payment Calendar - Expandable */}
                            {isExpanded && (
                              <div className="border-t border-[#27272A] p-4 bg-[#0D0D0D]" data-testid={`payment-calendar-panel-${customer.customer_id}`}>
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex items-center gap-3">
                                    <h4 className="text-white font-semibold text-sm uppercase tracking-wide">Payment Tracker</h4>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#39FF14]/10 text-[#39FF14] font-medium">
                                      {paidCount}/{totalSlots} {freq === 2 ? "cuts" : "months"} paid
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost" size="sm"
                                      className="h-7 w-7 p-0 text-[#A1A1AA] hover:text-white"
                                      onClick={() => changePaymentYear(-1)}
                                      data-testid="payment-year-prev"
                                    >
                                      <ChevronLeft className="w-4 h-4" />
                                    </Button>
                                    <span className="text-white font-bold text-sm min-w-[48px] text-center">{paymentYear}</span>
                                    <Button
                                      variant="ghost" size="sm"
                                      className="h-7 w-7 p-0 text-[#A1A1AA] hover:text-white"
                                      onClick={() => changePaymentYear(1)}
                                      data-testid="payment-year-next"
                                    >
                                      <ChevronRight className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* Month Grid */}
                                <div className={`grid gap-2 ${freq === 2 ? "grid-cols-3 sm:grid-cols-4 lg:grid-cols-6" : "grid-cols-4 sm:grid-cols-6 lg:grid-cols-12"}`}>
                                  {MONTH_NAMES.map((monthName, idx) => {
                                    const monthNum = idx + 1;
                                    const isCurrentMonth = new Date().getMonth() === idx && new Date().getFullYear() === paymentYear;
                                    
                                    if (freq === 2) {
                                      // 2x/month: show two sub-slots per month
                                      return (
                                        <div key={monthNum} className="space-y-1">
                                          <p className="text-[#A1A1AA] text-[10px] font-bold text-center uppercase">{monthName}</p>
                                          {[1, 2].map(cut => {
                                            const payKey = `${monthNum}_${cut}`;
                                            const isPaid = customerPayments[payKey]?.paid || false;
                                            const isToggling = togglingPayment === `${customer.customer_id}_${monthNum}_${cut}`;
                                            return (
                                              <button
                                                key={cut}
                                                data-testid={`payment-month-${customer.customer_id}-${monthNum}-cut${cut}`}
                                                onClick={() => toggleMonthPayment(customer.customer_id, monthNum, cut)}
                                                disabled={isToggling}
                                                className={`w-full flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all duration-200 cursor-pointer
                                                  ${isPaid 
                                                    ? 'bg-[#39FF14]/15 border-[#39FF14] text-[#39FF14] shadow-[0_0_8px_rgba(57,255,20,0.15)]' 
                                                    : isCurrentMonth
                                                      ? 'bg-[#121212] border-yellow-400/50 text-[#A1A1AA] hover:border-[#39FF14]/50 hover:bg-[#39FF14]/5'
                                                      : 'bg-[#121212] border-[#27272A] text-[#A1A1AA] hover:border-[#39FF14]/50 hover:bg-[#39FF14]/5'
                                                  } ${isToggling ? 'opacity-50' : ''}`}
                                              >
                                                {isToggling ? (
                                                  <div className="w-3 h-3 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin" />
                                                ) : isPaid ? (
                                                  <Check className="w-3 h-3" />
                                                ) : null}
                                                <span className="text-[10px] font-bold">Cut {cut}</span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      );
                                    }
                                    
                                    // 1x/month: single slot
                                    const isPaid = customerPayments[String(monthNum)]?.paid || false;
                                    const isToggling = togglingPayment === `${customer.customer_id}_${monthNum}_0`;
                                    
                                    return (
                                      <button
                                        key={monthNum}
                                        data-testid={`payment-month-${customer.customer_id}-${monthNum}`}
                                        onClick={() => toggleMonthPayment(customer.customer_id, monthNum)}
                                        disabled={isToggling}
                                        className={`relative flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all duration-200 cursor-pointer
                                          ${isPaid 
                                            ? 'bg-[#39FF14]/15 border-[#39FF14] text-[#39FF14] shadow-[0_0_8px_rgba(57,255,20,0.15)]' 
                                            : isCurrentMonth
                                              ? 'bg-[#121212] border-yellow-400/50 text-[#A1A1AA] hover:border-[#39FF14]/50 hover:bg-[#39FF14]/5'
                                              : 'bg-[#121212] border-[#27272A] text-[#A1A1AA] hover:border-[#39FF14]/50 hover:bg-[#39FF14]/5'
                                          }
                                          ${isToggling ? 'opacity-50' : ''}
                                        `}
                                      >
                                        {isToggling ? (
                                          <div className="w-4 h-4 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin" />
                                        ) : isPaid ? (
                                          <Check className="w-4 h-4 mb-1" />
                                        ) : (
                                          <div className="w-4 h-4 mb-1" />
                                        )}
                                        <span className="text-xs font-bold">{monthName}</span>
                                        {isPaid && (
                                          <span className="text-[10px] font-medium mt-0.5">PAID</span>
                                        )}
                                        {isCurrentMonth && !isPaid && (
                                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full" />
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                                
                                {/* Legend */}
                                <div className="flex items-center gap-4 mt-3 text-xs text-[#A1A1AA]">
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded bg-[#39FF14]/15 border border-[#39FF14]" />
                                    <span>Paid</span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded bg-[#121212] border border-[#27272A]" />
                                    <span>Unpaid</span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded bg-[#121212] border border-yellow-400/50 relative">
                                      <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-yellow-400 rounded-full" />
                                    </div>
                                    <span>Current Month</span>
                                  </div>
                                  <span className="ml-auto text-[#A1A1AA]/60">Click to toggle</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Plans Tab */}
              <TabsContent value="plans">
                <div className="space-y-6">
                  <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                    <h2 className="font-heading text-xl font-bold text-white uppercase mb-2">Edit Subscription Plans</h2>
                    <p className="text-[#A1A1AA] text-sm mb-6">Update pricing and discounts for your subscription plans</p>
                    
                    <div className="grid gap-6">
                      {Object.entries(plans).map(([planId, plan]) => (
                        <div 
                          key={planId}
                          className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6"
                        >
                          <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-4">
                              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                                planId === "yearly" ? "bg-[#39FF14]" : "bg-[#064E3B]"
                              }`}>
                                <CreditCard className={`w-7 h-7 ${planId === "yearly" ? "text-black" : "text-[#39FF14]"}`} />
                              </div>
                              <div>
                                <h3 className="font-heading text-xl font-bold text-white uppercase">{plan.name}</h3>
                                <p className="text-[#A1A1AA] text-sm">
                                  {planId === "yearly" ? "Annual billing" : "Monthly billing"}
                                </p>
                              </div>
                            </div>
                            {planId === "yearly" && (
                              <span className="bg-[#39FF14] text-black text-xs font-bold px-3 py-1 rounded-full">
                                BEST VALUE
                              </span>
                            )}
                          </div>

                          <div className="grid md:grid-cols-2 gap-4 mb-6">
                            <div>
                              <Label className="text-[#A1A1AA] text-sm">Plan Name</Label>
                              <p className="text-white text-lg font-medium">{plan.name}</p>
                            </div>
                            <div>
                              <Label className="text-[#A1A1AA] text-sm">Discount Applied</Label>
                              <p className="text-[#39FF14] text-2xl font-bold">{plan.discount_percent}%</p>
                            </div>
                          </div>

                          <div className="mb-4">
                            <Label className="text-[#A1A1AA] text-sm">Features</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {plan.features?.map((feature, i) => (
                                <span 
                                  key={i}
                                  className="bg-[#121212] text-[#A1A1AA] text-xs px-3 py-1 rounded-full border border-[#27272A]"
                                >
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>

                          <Button
                            onClick={() => openEditDialog(planId)}
                            className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                          >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit Plan
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings">
                <div className="space-y-6">
                  <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                    <h2 className="font-heading text-xl font-bold text-white uppercase mb-2">Subscription Settings</h2>
                    <p className="text-[#A1A1AA] text-sm mb-6">Control subscription visibility across your website</p>
                    
                    {/* Subscriptions Toggle */}
                    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
                            subscriptionsEnabled ? "bg-[#39FF14]" : "bg-[#27272A]"
                          }`}>
                            {subscriptionsEnabled ? (
                              <ToggleRight className="w-7 h-7 text-black" />
                            ) : (
                              <ToggleLeft className="w-7 h-7 text-[#A1A1AA]" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-heading text-lg font-bold text-white">
                              Subscription Plans {subscriptionsEnabled ? "Enabled" : "Disabled"}
                            </h3>
                            <p className="text-[#A1A1AA] text-sm">
                              {subscriptionsEnabled 
                                ? "Customers can see and subscribe to plans" 
                                : "Subscription options are hidden from customers"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch
                            data-testid="subscriptions-toggle"
                            checked={subscriptionsEnabled}
                            onCheckedChange={handleToggleSubscriptions}
                            disabled={togglingSubscriptions}
                            className="data-[state=checked]:bg-[#39FF14]"
                          />
                        </div>
                      </div>
                      
                      {!subscriptionsEnabled && (
                        <div className="mt-4 p-4 bg-yellow-400/10 border border-yellow-400/30 rounded-lg">
                          <p className="text-yellow-400 text-sm flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            When disabled, the following are hidden:
                          </p>
                          <ul className="text-[#A1A1AA] text-sm mt-2 ml-6 space-y-1 list-disc">
                            <li>Subscription plans on the landing page</li>
                            <li>Subscribe options in the customer portal</li>
                            <li>Monthly/Yearly pricing on quotes</li>
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    {/* Stripe Checkout Toggle */}
                    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6 mt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
                            stripeCheckoutEnabled ? "bg-[#635BFF]" : "bg-[#39FF14]"
                          }`}>
                            {stripeCheckoutEnabled ? (
                              <CreditCard className="w-7 h-7 text-white" />
                            ) : (
                              <MessageCircle className="w-7 h-7 text-black" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-heading text-lg font-bold text-white">
                              {stripeCheckoutEnabled ? "Stripe Checkout" : "Request Mode"}
                            </h3>
                            <p className="text-[#A1A1AA] text-sm">
                              {stripeCheckoutEnabled 
                                ? "Customers pay directly via Stripe" 
                                : "Customers send you a subscription request message"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch
                            data-testid="stripe-checkout-toggle"
                            checked={stripeCheckoutEnabled}
                            onCheckedChange={handleToggleStripeCheckout}
                            disabled={togglingStripe}
                            className="data-[state=checked]:bg-[#635BFF]"
                          />
                        </div>
                      </div>
                      
                      <div className={`mt-4 p-4 rounded-lg ${stripeCheckoutEnabled ? 'bg-[#635BFF]/10 border border-[#635BFF]/30' : 'bg-[#39FF14]/10 border border-[#39FF14]/30'}`}>
                        <p className={`text-sm flex items-center gap-2 ${stripeCheckoutEnabled ? 'text-[#635BFF]' : 'text-[#39FF14]'}`}>
                          {stripeCheckoutEnabled ? <CreditCard className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
                          {stripeCheckoutEnabled ? "When customer clicks Subscribe:" : "When customer clicks Subscribe:"}
                        </p>
                        <ul className="text-[#A1A1AA] text-sm mt-2 ml-6 space-y-1 list-disc">
                          {stripeCheckoutEnabled ? (
                            <>
                              <li>Redirects to Stripe checkout page</li>
                              <li>Customer enters payment details</li>
                              <li>Subscription activates automatically</li>
                            </>
                          ) : (
                            <>
                              <li>Sends you a notification</li>
                              <li>Creates a chat message from customer</li>
                              <li>You manually set up their subscription</li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                    
                    {/* Current Status */}
                    <div className="mt-6 grid grid-cols-2 gap-4">
                      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                        <p className="text-[#A1A1AA] text-xs uppercase mb-1">Active Subscribers</p>
                        <p className="text-white text-2xl font-bold">{customersWithSubscription.length}</p>
                      </div>
                      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                        <p className="text-[#A1A1AA] text-xs uppercase mb-1">Status</p>
                        <p className={`text-2xl font-bold ${subscriptionsEnabled ? "text-[#39FF14]" : "text-yellow-400"}`}>
                          {subscriptionsEnabled ? "Active" : "Hidden"}
                        </p>
                      </div>
                    </div>

                    {/* Additional Services Toggle */}
                    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6 mt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
                            additionalServicesEnabled ? "bg-[#39FF14]" : "bg-[#27272A]"
                          }`}>
                            <Wrench className={`w-7 h-7 ${additionalServicesEnabled ? "text-black" : "text-[#A1A1AA]"}`} />
                          </div>
                          <div>
                            <h3 className="font-heading text-lg font-bold text-white">
                              Additional Services {additionalServicesEnabled ? "Visible" : "Hidden"}
                            </h3>
                            <p className="text-[#A1A1AA] text-sm">
                              {additionalServicesEnabled 
                                ? "Trash bin cleaning & gutter cleaning are shown on your site" 
                                : "Additional services section is hidden from customers"}
                            </p>
                          </div>
                        </div>
                        <Switch
                          data-testid="additional-services-toggle"
                          checked={additionalServicesEnabled}
                          onCheckedChange={handleToggleAdditionalServices}
                          disabled={togglingAdditionalServices}
                          className="data-[state=checked]:bg-[#39FF14]"
                        />
                      </div>
                    </div>

                    {/* Neighborhood Discount Toggle + Editor */}
                    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6 mt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
                            neighborhoodDiscountEnabled ? "bg-[#39FF14]" : "bg-[#27272A]"
                          }`}>
                            <Tag className={`w-7 h-7 ${neighborhoodDiscountEnabled ? "text-black" : "text-[#A1A1AA]"}`} />
                          </div>
                          <div>
                            <h3 className="font-heading text-lg font-bold text-white">
                              Neighborhood Discount {neighborhoodDiscountEnabled ? "Visible" : "Hidden"}
                            </h3>
                            <p className="text-[#A1A1AA] text-sm">
                              {neighborhoodDiscountEnabled 
                                ? "Floating coupon widget is shown on your landing page" 
                                : "Coupon widget is hidden from visitors"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {neighborhoodDiscountEnabled && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[#39FF14]/30 text-[#39FF14] hover:bg-[#39FF14]/10"
                              onClick={() => setCouponEditing(!couponEditing)}
                              data-testid="coupon-edit-toggle"
                            >
                              <Edit3 className="w-4 h-4 mr-1" />
                              {couponEditing ? "Close" : "Edit"}
                            </Button>
                          )}
                          <Switch
                            data-testid="neighborhood-discount-toggle"
                            checked={neighborhoodDiscountEnabled}
                            onCheckedChange={handleToggleNeighborhoodDiscount}
                            disabled={togglingNeighborhoodDiscount}
                            className="data-[state=checked]:bg-[#39FF14]"
                          />
                        </div>
                      </div>

                      {/* Inline Coupon Editor */}
                      {couponEditing && neighborhoodDiscountEnabled && (
                        <div className="mt-6 border-t border-[#27272A] pt-6 space-y-5" data-testid="coupon-editor">
                          {/* Live Preview */}
                          <div className="mb-4">
                            <p className="text-[#A1A1AA] text-xs uppercase tracking-wider font-bold mb-3">Live Preview</p>
                            <div className="relative w-[320px] mx-auto bg-[#0A0A0A] border-2 border-dashed border-[#39FF14] rounded-2xl overflow-hidden">
                              <div className="pt-4 px-4">
                                <span className="inline-block px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-[#39FF14]/20 text-[#39FF14] border border-[#39FF14]/40">
                                  {couponFields.badge}
                                </span>
                              </div>
                              <div className="px-4 pb-1 pt-2">
                                <h3 className="text-white font-bold text-base leading-tight">{couponFields.title}</h3>
                                <p className="text-[#A1A1AA] text-[10px] mt-0.5">{couponFields.subtitle}</p>
                              </div>
                              <div className="px-4 py-2">
                                <div className="border-t border-dashed border-[#39FF14]/40"></div>
                              </div>
                              <div className="px-4 pb-2">
                                <div className="bg-[#39FF14]/5 border border-[#39FF14]/20 rounded-xl p-2.5 text-center">
                                  <p className="text-[#39FF14] text-lg font-black">{couponFields.discount_text}</p>
                                  <p className="text-[#A1A1AA] text-[10px] mt-0.5 leading-relaxed">{couponFields.description}</p>
                                </div>
                              </div>
                              <div className="px-4 pb-2">
                                <div className="w-full bg-[#39FF14] text-black text-center text-xs font-bold py-2 rounded-full">
                                  {couponFields.cta_button}
                                </div>
                              </div>
                              <div className="px-4 pb-3">
                                <p className="text-[#52525B] text-[9px] text-center">{couponFields.fine_print}</p>
                              </div>
                            </div>
                          </div>

                          {/* Editable Fields */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">Badge Text</Label>
                              <Input
                                data-testid="coupon-field-badge"
                                value={couponFields.badge}
                                onChange={e => setCouponFields(p => ({ ...p, badge: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                                placeholder="SPECIAL OFFER"
                              />
                            </div>
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">Title</Label>
                              <Input
                                data-testid="coupon-field-title"
                                value={couponFields.title}
                                onChange={e => setCouponFields(p => ({ ...p, title: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                                placeholder="Neighborhood Discount"
                              />
                            </div>
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">Subtitle</Label>
                              <Input
                                data-testid="coupon-field-subtitle"
                                value={couponFields.subtitle}
                                onChange={e => setCouponFields(p => ({ ...p, subtitle: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                                placeholder="Save when your neighbors book too!"
                              />
                            </div>
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">Discount Text</Label>
                              <Input
                                data-testid="coupon-field-discount-text"
                                value={couponFields.discount_text}
                                onChange={e => setCouponFields(p => ({ ...p, discount_text: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                                placeholder="Up to 15% OFF"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <Label className="text-[#A1A1AA] text-xs">Description</Label>
                              <Textarea
                                data-testid="coupon-field-description"
                                value={couponFields.description}
                                onChange={e => setCouponFields(p => ({ ...p, description: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1 resize-none"
                                rows={2}
                                placeholder="Describe the discount..."
                              />
                            </div>
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">Button Text</Label>
                              <Input
                                data-testid="coupon-field-cta"
                                value={couponFields.cta_button}
                                onChange={e => setCouponFields(p => ({ ...p, cta_button: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                                placeholder="Claim Discount"
                              />
                            </div>
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">Fine Print</Label>
                              <Input
                                data-testid="coupon-field-fine-print"
                                value={couponFields.fine_print}
                                onChange={e => setCouponFields(p => ({ ...p, fine_print: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                                placeholder="Terms and conditions..."
                              />
                            </div>
                          </div>

                          {/* Save / Cancel */}
                          <div className="flex justify-end gap-3 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[#27272A] text-[#A1A1AA] hover:text-white"
                              onClick={() => setCouponEditing(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                              onClick={handleSaveCoupon}
                              disabled={couponSaving}
                              data-testid="coupon-save-btn"
                            >
                              <Save className="w-4 h-4 mr-1" />
                              {couponSaving ? "Saving..." : "Save Changes"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Vonage SMS Configuration */}
                    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6 mt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
                            vonageConfig.enabled ? "bg-[#39FF14]" : "bg-[#27272A]"
                          }`}>
                            <MessageSquare className={`w-7 h-7 ${vonageConfig.enabled ? "text-black" : "text-[#A1A1AA]"}`} />
                          </div>
                          <div>
                            <h3 className="font-heading text-lg font-bold text-white">
                              Vonage SMS {vonageConfig.enabled ? "Active" : "Inactive"}
                            </h3>
                            <p className="text-[#A1A1AA] text-sm">
                              {vonageConfig.enabled
                                ? "Texts you on every new quote / cut request, and sends customer SMS"
                                : "Add your Vonage key + secret to get a text for every new quote / cut request"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#39FF14]/30 text-[#39FF14] hover:bg-[#39FF14]/10"
                            onClick={() => setVonageExpanded(!vonageExpanded)}
                            data-testid="vonage-config-toggle"
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            {vonageExpanded ? "Close" : "Configure"}
                          </Button>
                          <Switch
                            data-testid="vonage-enabled-toggle"
                            checked={vonageConfig.enabled}
                            onCheckedChange={(checked) => setVonageConfig(p => ({ ...p, enabled: checked }))}
                            className="data-[state=checked]:bg-[#39FF14]"
                          />
                        </div>
                      </div>

                      {vonageExpanded && (
                        <div className="mt-6 border-t border-[#27272A] pt-6 space-y-4" data-testid="vonage-config-form">
                          <p className="text-[#A1A1AA] text-xs">
                            Get your credentials at <a href="https://dashboard.nexmo.com/settings" target="_blank" rel="noopener noreferrer" className="text-[#39FF14] underline">dashboard.nexmo.com/settings</a>
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">API Key</Label>
                              <Input
                                data-testid="vonage-api-key"
                                value={vonageConfig.api_key}
                                onChange={e => setVonageConfig(p => ({ ...p, api_key: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                                placeholder="abc12345"
                              />
                            </div>
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">API Secret</Label>
                              <Input
                                data-testid="vonage-api-secret"
                                type="password"
                                value={vonageConfig.api_secret}
                                onChange={e => setVonageConfig(p => ({ ...p, api_secret: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                                placeholder="Your API secret"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-[#A1A1AA] text-xs">From Number (Vonage virtual number)</Label>
                            <Input
                              data-testid="vonage-from-number"
                              value={vonageConfig.from_number}
                              onChange={e => setVonageConfig(p => ({ ...p, from_number: e.target.value }))}
                              className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                              placeholder="Vonage number or sender name"
                            />
                          </div>
                          <div>
                            <Label className="text-[#A1A1AA] text-xs">Alert phone numbers (get a text for every new quote / cut request)</Label>
                            <Input
                              data-testid="alert-phone-numbers"
                              value={alertNumbers}
                              onChange={e => setAlertNumbers(e.target.value)}
                              className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                              placeholder="+819068667210, +819068610100"
                            />
                          </div>
                          <div className="flex justify-end gap-3 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10"
                              onClick={handleTestText}
                              disabled={testingText}
                              data-testid="vonage-test-btn"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              {testingText ? "Sending..." : "Send Test Text"}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[#27272A] text-[#A1A1AA] hover:text-white"
                              onClick={() => setVonageExpanded(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                              onClick={handleSaveVonage}
                              disabled={savingVonage}
                              data-testid="vonage-save-btn"
                            >
                              <Save className="w-4 h-4 mr-1" />
                              {savingVonage ? "Saving..." : "Save Vonage Settings"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Gmail SMTP Configuration */}
                    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6 mt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
                            gmailConfig.enabled ? "bg-[#39FF14]" : "bg-[#27272A]"
                          }`}>
                            <Send className={`w-7 h-7 ${gmailConfig.enabled ? "text-black" : "text-[#A1A1AA]"}`} />
                          </div>
                          <div>
                            <h3 className="font-heading text-lg font-bold text-white">
                              Gmail Email {gmailConfig.enabled ? "Active" : "Inactive"}
                            </h3>
                            <p className="text-[#A1A1AA] text-sm">
                              {gmailConfig.enabled
                                ? `Emails sent from ${gmailConfig.email}`
                                : "Configure Gmail to auto-send emails to customers"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#39FF14]/30 text-[#39FF14] hover:bg-[#39FF14]/10"
                            onClick={() => setGmailExpanded(!gmailExpanded)}
                            data-testid="gmail-config-toggle"
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            {gmailExpanded ? "Close" : "Configure"}
                          </Button>
                          <Switch
                            data-testid="gmail-enabled-toggle"
                            checked={gmailConfig.enabled}
                            onCheckedChange={(checked) => setGmailConfig(p => ({ ...p, enabled: checked }))}
                            className="data-[state=checked]:bg-[#39FF14]"
                          />
                        </div>
                      </div>

                      {gmailExpanded && (
                        <div className="mt-6 border-t border-[#27272A] pt-6 space-y-4" data-testid="gmail-config-form">
                          <p className="text-[#A1A1AA] text-xs">
                            Use a <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-[#39FF14] underline">Google App Password</a> for best security (requires 2-Step Verification)
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">Gmail Address</Label>
                              <Input
                                data-testid="gmail-email"
                                value={gmailConfig.email}
                                onChange={e => setGmailConfig(p => ({ ...p, email: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                                placeholder="zigzaglawn@gmail.com"
                              />
                            </div>
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">Password / App Password</Label>
                              <Input
                                data-testid="gmail-password"
                                type="password"
                                value={gmailConfig.password}
                                onChange={e => setGmailConfig(p => ({ ...p, password: e.target.value }))}
                                className="bg-[#1A1A1A] border-[#27272A] text-white mt-1"
                                placeholder="Your password or app password"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-3 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[#27272A] text-[#A1A1AA] hover:text-white"
                              onClick={() => setGmailExpanded(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                              onClick={handleSaveGmail}
                              disabled={savingGmail}
                              data-testid="gmail-save-btn"
                            >
                              <Save className="w-4 h-4 mr-1" />
                              {savingGmail ? "Saving..." : "Save Gmail Settings"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>

      {/* Edit Plan Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Edit Plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Plan Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                placeholder="e.g., Monthly Plan"
              />
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Discount Percentage (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={editForm.discount_percent}
                onChange={(e) => setEditForm({ ...editForm, discount_percent: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                placeholder="e.g., 10"
              />
              <p className="text-[#A1A1AA]/60 text-xs mt-1">This discount will be applied to each customer's quoted price</p>
            </div>
            <Button 
              className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
              onClick={handleSavePlan}
              disabled={saving}
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Subscription Dialog */}
      <Dialog open={editSubDialogOpen} onOpenChange={setEditSubDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Edit Subscription</DialogTitle>
          </DialogHeader>
          {editingSubscription && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] rounded-lg">
                <div className="w-12 h-12 bg-[#064E3B] rounded-full flex items-center justify-center">
                  <span className="text-[#39FF14] font-bold text-lg">
                    {editingSubscription.name?.charAt(0).toUpperCase() || "C"}
                  </span>
                </div>
                <div>
                  <h3 className="text-white font-semibold">{editingSubscription.name}</h3>
                  <p className="text-[#A1A1AA] text-sm">{editingSubscription.email}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-[#A1A1AA]">Subscription Plan</Label>
                <Select value={newPlanForEdit} onValueChange={setNewPlanForEdit}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    <SelectItem value="monthly" className="text-white hover:bg-[#39FF14]/20">
                      Monthly (10% off)
                    </SelectItem>
                    <SelectItem value="yearly" className="text-white hover:bg-[#39FF14]/20">
                      Yearly (25% off) - Best Value
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-[#A1A1AA]">Frequency</Label>
                <Select value={newFrequencyForEdit} onValueChange={setNewFrequencyForEdit}>
                  <SelectTrigger data-testid="edit-subscription-frequency-select" className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    <SelectItem value="1" className="text-white hover:bg-[#39FF14]/20">
                      1x per month
                    </SelectItem>
                    <SelectItem value="2" className="text-white hover:bg-[#39FF14]/20">
                      2x per month
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  className="flex-1 border-[#27272A] text-[#A1A1AA] hover:text-white"
                  onClick={() => setEditSubDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                  onClick={handleSaveSubscriptionEdit}
                  disabled={savingSubEdit}
                >
                  {savingSubEdit ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Chat Dialog */}
      <Dialog open={chatDialogOpen} onOpenChange={setChatDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-[#39FF14]" />
              Chat with {chatCustomer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col h-[400px]">
            {/* Messages List */}
            <div className="flex-1 overflow-y-auto space-y-3 p-4 bg-[#0A0A0A] rounded-lg mb-4">
              {messages.length === 0 ? (
                <div className="text-center text-[#A1A1AA] py-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No messages yet</p>
                  <p className="text-sm">Start a conversation with {chatCustomer?.name}</p>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div
                    key={msg.message_id || idx}
                    className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'} group`}
                  >
                    <div className={`max-w-[80%] rounded-lg px-4 py-2 ${
                      msg.sender_type === 'admin'
                        ? 'bg-[#39FF14] text-black'
                        : 'bg-[#27272A] text-white'
                    }`}>
                      <p className="text-sm">{msg.message}</p>
                      <div className={`flex items-center justify-between mt-1 ${msg.sender_type === 'admin' ? 'text-black/60' : 'text-[#A1A1AA]'}`}>
                        <p className="text-xs">
                          {msg.sender_name} • {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <button
                          onClick={() => handleDeleteMessage(msg.message_id)}
                          className={`ml-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-black/20 ${
                            msg.sender_type === 'admin' ? 'text-black/60 hover:text-black' : 'text-[#A1A1AA] hover:text-red-400'
                          }`}
                          title="Delete message"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>
            
            {/* Message Input */}
            <div className="flex gap-2">
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14] min-h-[60px] max-h-[100px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <Button
                onClick={handleSendMessage}
                disabled={sendingMessage || !newMessage.trim()}
                className="bg-[#39FF14] text-black hover:bg-[#32D711] px-4"
              >
                {sendingMessage ? (
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscriptions;
